import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, where, orderBy, doc, getDoc } from 'firebase/firestore';
import { db } from '../../pages/app/protected/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { quotas } from '../../pages/app/protected/services/QuotasConfig';

export default function InvoiceSummary() {
  const { user ,companyId} = useAuth();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [companySnap, setCompanySnap] = useState({});
  const perPage = 10;
  const [plan, setPlan] = useState('');
  const [loading, setLoading] = useState(true);

const loadPlan = async () => {
  const docRef = doc(db, 'companies', companyId);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    setPlan(docSnap.data()?.subscription?.plan || 'Free Trial');
  }
};

  useEffect(() => {
    loadPlan();
    // Set today's date as default for both from and to date
    const today = new Date().toISOString().split('T')[0];
    setFromDate(today);
    setToDate(today);
  }, []);

  const filteredInvoices = invoices.filter((inv) => {
    const invDate = new Date(inv.timestamp);
    const from = fromDate ? new Date(fromDate) : new Date(0);
    const to = toDate ? new Date(toDate) : new Date();
    
    // Set to to end of day
    to.setHours(23, 59, 59, 999);

    const matchesSearch = String(inv.receiptNumber).includes(search);
    const matchesDate = invDate >= from && invDate <= to;
    return matchesSearch && matchesDate;
  });

  const totalPages = Math.max(1, Math.ceil(filteredInvoices.length / perPage));
  const paginated = filteredInvoices.slice((currentPage - 1) * perPage, currentPage * perPage);

  const loadInvoices = async () => {
    if (!user || !companyId) return;
    const q = query(
      collection(db, 'companies', companyId, 'orders'),
      orderBy('paymentStatus'),
      orderBy('timestamp', 'desc')
    );
    const snapshot = await getDocs(q);
    const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setInvoices(list);
  };

  const loadCompany = async () => {
    const profileRef = doc(db, 'companies', companyId, 'companyProfile', 'info');
    const profileSnap = await getDoc(profileRef);
    if (profileSnap.exists()) {
      setCompanySnap(profileSnap.data());
    }
  };

  const formatDate = (date) => {
    const d = new Date(date);
    return `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1)
      .toString()
      .padStart(2, '0')}/${d.getFullYear()}`;
  };

  const formatTime = (date) => {
    const d = new Date(date);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const toNumber = (val) => {
    const num = Number(val);
    return Number.isFinite(num) ? num : 0;
  };

  // Prefer explicit tax breakdown if present; otherwise fall back to stored taxAmount
  const getTaxAmount = (inv) => {
    const breakdownSum =
      toNumber(inv.sst6Amount) +
      toNumber(inv.sst8Amount) +
      toNumber(inv.serviceTax10Amount);

    const totalsSum = inv.totals
      ? toNumber(inv.totals.sst6Amount) +
        toNumber(inv.totals.sst8Amount) +
        toNumber(inv.totals.serviceTax10Amount)
      : 0;

    if (breakdownSum > 0) return breakdownSum;
    if (totalsSum > 0) return totalsSum;
    if (inv.taxAmount !== undefined && inv.taxAmount !== null) return toNumber(inv.taxAmount);
    return 0;
  };

  const exportToPDF = async () => {
    if (!quotas[plan]?.exportPDF) {
      alert('Export to PDF is only available for Plus and Premium plans.');
      return;
    }
  
    const doc = new jsPDF();
  
    const fromFormatted = formatDate(fromDate);
    const toFormatted = formatDate(toDate);
    const title = `Invoice Summary Report (${fromFormatted} - ${toFormatted})`;
  
    // Company name only
    doc.setFontSize(12);
    doc.text(companySnap.companyName || 'Company Name', 105, 20, { align: 'center' });
  
    doc.setFontSize(10);
    doc.text(title, 105, 28, { align: 'center' });
  
    const headers = [['Date', 'Receipt #', 'Payment', 'Subtotal', 'Tax', 'Discount', 'Total', 'Status']];
    const data = filteredInvoices.map((inv) => [
      formatDate(inv.timestamp),
      `#${inv.receiptNumber}`,
      inv.paymentMethod,
      `RM ${inv.subtotal.toFixed(2)}`,
      `RM ${getTaxAmount(inv).toFixed(2)}`,
      `RM ${inv.discountAmount?.toFixed(2) || '0.00'}`,
      `RM ${inv.total.toFixed(2)}`,
      inv.paymentStatus,
    ]);
  
    autoTable(doc, {
      head: headers,
      body: data,
      startY: 35,
      styles: { fontSize: 8 },
    });
  
    // Footer
    const pageHeight = doc.internal.pageSize.height;
    doc.setFontSize(9);
    doc.text('Powered by CashMate', 105, pageHeight - 10, { align: 'center' });
  
    const cleanFilename = `invoice-summary_${fromFormatted.replace(/\//g, '-')}_to_${toFormatted.replace(/\//g, '-')}.pdf`;
    doc.save(cleanFilename);
  };

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([loadInvoices(), loadCompany()]);
      setLoading(false);
    };
    loadAll();
  }, [user]);

  if (loading) {
    return (
      <div className="w-full flex flex-col justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-purple-500 mb-4"></div>
        <span className="text-white">Loading information...</span>
      </div>
    );
  }

  return (
    <div className="p-6 text-white">
      <div className="flex items-center mb-4 gap-2">
        <button onClick={() => navigate(-1)} className="text-sm flex items-center gap-1 text-gray-300 hover:text-white">
          <span className="text-lg">←</span> Back
        </button>
      </div>

      <h2 className="text-2xl font-bold mb-4">Invoice Summary</h2>

      <div className="flex gap-4 mb-6">
        <div className="flex gap-2">
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="px-3 py-2 bg-[#1f1f1f] rounded text-white"
          />
          <span className="text-gray-400">to</span>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="px-3 py-2 bg-[#1f1f1f] rounded text-white"
          />
        </div>
        <input
          type="text"
          placeholder="Search by receipt number"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="px-3 py-2 bg-[#1f1f1f] rounded text-white"
        />
        <button onClick={exportToPDF} className="bg-purple-600 px-4 py-2 rounded">Export to PDF</button>
      </div>

      <div className="bg-[#1a1a1a] rounded-lg overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-gray-400">
              <th className="px-4 py-2">Date</th>
              <th className="px-4 py-2">Receipt #</th>
              <th className="px-4 py-2">Payment</th>
              <th className="px-4 py-2">Subtotal</th>
              <th className="px-4 py-2">Tax</th>
              <th className="px-4 py-2">Discount</th>
              <th className="px-4 py-2">Total</th>
              <th className="px-4 py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {paginated.map((inv) => (
              <tr key={inv.id} className="border-t border-gray-700">
                <td className="px-4 py-2">{formatDate(inv.timestamp) + ' ' + formatTime(inv.timestamp)}</td>
                <td className="px-4 py-2">#{inv.receiptNumber}</td>
                <td className="px-4 py-2">{inv.paymentMethod}</td>
                <td className="px-4 py-2">RM {inv.subtotal.toFixed(2)}</td>
                <td className="px-4 py-2">RM {getTaxAmount(inv).toFixed(2)}</td>
                <td className="px-4 py-2">RM {inv.discountAmount?.toFixed(2) || '0.00'}</td>
                <td className="px-4 py-2 font-bold">RM {inv.total.toFixed(2)}</td>
                <td className="px-4 py-2">{inv.paymentStatus}</td>
              </tr>
            ))}
            {paginated.length === 0 && (
              <tr><td className="px-4 py-6 text-center text-gray-400" colSpan={9}>No records found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex justify-center flex-wrap mt-6 gap-2">
        <button className="px-4 py-2 bg-[#2c2c2c] text-white rounded disabled:opacity-50" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>Prev</button>
        {[...Array(totalPages)].map((_, i) => (
          <button key={i} className={`px-4 py-2 rounded ${currentPage === i + 1 ? 'bg-purple-600 text-white' : 'bg-[#2c2c2c] text-gray-300'}`} onClick={() => setCurrentPage(i + 1)}>{i + 1}</button>
        ))}
        <button className="px-4 py-2 bg-[#2c2c2c] text-white rounded disabled:opacity-50" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>Next</button>
      </div>
    </div>
  );
}
